import * as React from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { toast } from "sonner";

import { FIREBASE_ENABLED } from "@/config";
import { getFirebaseAuth } from "@/lib/firebase";
import { fetchSelf, hasPartnerAccess, isSuperAdmin as rolesIncludeSuperAdmin } from "@/lib/auth";
import { isBackendUnavailable } from "@/lib/backend-error";
import {
  TOKEN_DEFS,
  clone,
  makeTerms,
  makeTheme,
  type Brand,
  type NewBrandInput,
  type Terms,
  type Theme,
  type TokenDef,
} from "@/data/config";
import {
  fetchMyBrands,
  fetchBrand,
  type BrandData,
  createBrand as apiCreateBrand,
  deleteBrand as apiDeleteBrand,
  createTheme as apiCreateTheme,
  updateTheme as apiUpdateTheme,
  deleteTheme as apiDeleteTheme,
  setActiveTheme as apiSetActiveTheme,
  publishBrandTheme as apiPublishBrandTheme,
  uploadThemeLogo as apiUploadThemeLogo,
  toUiBrand,
  toUiTheme,
  toThemeContentInput,
  toTerminologyInput,
} from "@/data/brands-api";

/** Resolve the theme to edit for a brand: prefer the requested id, then the
    brand's live theme, then its first theme. A brand always has ≥1 theme. */
function resolveTheme(brand: Brand, themeId?: string): Theme {
  return (
    (themeId && brand.themes.find((t) => t.id === themeId)) ||
    brand.themes.find((t) => t.id === brand.activeThemeId) ||
    brand.themes[0]!
  );
}

/** Best-effort message from a thrown GraphQL/network error. */
function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}

interface Draft {
  theme: Theme;
  terms: Terms;
}

interface PortalContextValue {
  // auth
  authed: boolean;
  /** False until the initial Firebase auth state (and role check) resolves. */
  authReady: boolean;
  /** True while a signed-in user's partner access is being verified. */
  authChecking: boolean;
  /** Set when a Firebase sign-in succeeded but the account lacks portal access. */
  authError: string | null;
  /**
   * True when the user is signed in to Firebase but the backend GraphQL endpoint
   * is unreachable, so the portal can't load. The session is kept; an
   * interstitial is shown and {@link retryBackend} re-attempts the check.
   */
  backendUnavailable: boolean;
  retryBackend: () => void;
  /**
   * True when the user is signed in to Firebase but has no portal access (not a
   * partner and not a member of any brand). They stay signed in and see the
   * partners-only page; they leave via the explicit sign-out button.
   */
  accessDenied: boolean;
  user: { name: string; email: string; avatarUrl: string; username: string };
  clearAuthError: () => void;
  logout: () => void;
  // brands
  brands: Brand[];
  activeId: string;
  activeBrand: Brand | undefined;
  /**
   * True when the active brand isn't one the viewer belongs to — i.e. a super
   * admin is viewing/editing someone else's brand. Drives the "admin view"
   * banner and the extra publish confirmation.
   */
  viewingOthersBrand: boolean;
  /** Whether the signed-in user may create brands (has the global `partner` role). */
  canCreateBrand: boolean;
  /** Whether the signed-in user is a super admin (unlocks the "All brands" admin
   *  directory and cross-brand membership management). */
  isSuperAdmin: boolean;
  /**
   * Ensure a brand is loaded into local state so it can be edited. Returns true
   * if it's available (already loaded, or freshly fetched — including brands the
   * viewer doesn't belong to, for super admins), false if it can't be reached.
   * Used by {@link BrandRoute} to resolve the `:brandId` in the URL.
   */
  resolveBrand: (brandId: string) => Promise<boolean>;
  /**
   * Make a (already-loaded) brand active and open one of its themes into the
   * draft editor. Driven by the URL (`:brandId` + `?theme`) via BrandRoute — the
   * switchers navigate rather than calling this directly.
   */
  applySelection: (brandId: string, themeId?: string) => void;
  /** Seed a brand into local state from data already in hand (e.g. an admin
   *  directory row), so navigating to it doesn't trigger a redundant refetch. */
  cacheBrand: (brand: BrandData) => void;
  /** Create a brand. Resolves to the new brand's id on success, else null. */
  commitBrand: (input: NewBrandInput) => Promise<string | null>;
  /** Delete a brand (owner only). Resolves to the next brand's id to navigate
   *  to, or null when none remain (caller routes to the create flow). */
  deleteBrand: (brandId: string) => Promise<string | null>;
  // themes (within the active brand)
  /** Id of the theme currently open in the editor (not necessarily the live one). */
  selectedThemeId: string;
  /** Add a theme to the active brand. Resolves to the new theme's id (so the
   *  caller can navigate `?theme=` to it), or undefined on failure. */
  addTheme: () => Promise<string | undefined>;
  renameTheme: (themeId: string, name: string) => Promise<void>;
  /** Delete a theme. Resolves to the id the editor should switch to when the
   *  deleted theme was the selected one, else undefined (no navigation needed). */
  deleteTheme: (themeId: string) => Promise<string | undefined>;
  setLiveTheme: (themeId: string) => Promise<void>;
  // draft editing
  draft: Draft;
  dirty: boolean;
  saving: boolean;
  lastSaved: boolean;
  onThemeChange: (key: string, value: string | null) => void;
  onTermChange: (key: keyof Terms, value: string) => void;
  save: () => Promise<void>;
  discard: () => void;
  // editable token list
  defs: TokenDef[];
  /** Bumped after each successful publish so embedded previews can reload — the
   *  live app doesn't poll for brand/theme changes. */
  previewReloadKey: number;
  // welcome banner after registration
  welcome: string | null;
  dismissWelcome: () => void;
}

const PortalContext = React.createContext<PortalContextValue | null>(null);

const GUEST_USER = { name: "", email: "", avatarUrl: "", username: "" };

export function PortalProvider({ children }: { children: React.ReactNode }) {
  // Auth is backed by the shared pop.in Firebase project. `authReady` starts
  // true when Firebase isn't configured so the app renders the (signed-out)
  // login screen instead of hanging on a perpetual loading state.
  const [authed, setAuthed] = React.useState(false);
  const [authReady, setAuthReady] = React.useState(!FIREBASE_ENABLED);
  const [authChecking, setAuthChecking] = React.useState(false);
  const [authError, setAuthError] = React.useState<string | null>(null);
  const [backendUnavailable, setBackendUnavailable] = React.useState(false);
  const [accessDenied, setAccessDenied] = React.useState(false);
  const [user, setUser] = React.useState(GUEST_USER);
  const [welcome] = React.useState<string | null>(null);
  // Brands are loaded from the backend after sign-in (see the auth effect).
  const [brands, setBrands] = React.useState<Brand[]>([]);
  const [canCreateBrand, setCanCreateBrand] = React.useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = React.useState(false);
  const [activeId, setActiveId] = React.useState("");
  const [selectedThemeId, setSelectedThemeId] = React.useState("");
  const [draft, setDraft] = React.useState<Draft>(() => ({ theme: makeTheme(), terms: makeTerms() }));
  const [previewReloadKey, setPreviewReloadKey] = React.useState(0);
  const [saving, setSaving] = React.useState(false);
  const [lastSaved, setLastSaved] = React.useState(false);

  // Mirror `brands` in a ref so the URL-driven resolvers (resolveBrand /
  // applySelection) can read the latest list without taking it as a dependency
  // — otherwise the BrandRoute sync effect would re-run on every brand mutation
  // (save, addTheme, …) and clobber the open draft.
  const brandsRef = React.useRef(brands);
  brandsRef.current = brands;

  const activeBrand = brands.find((b) => b.id === activeId);
  // viewerRole is null only when the viewer isn't a member of the brand — which,
  // since non-members can't otherwise load a brand, means a super admin opened
  // someone else's brand from the "All brands" directory.
  const viewingOthersBrand = !!activeBrand && activeBrand.viewerRole == null;
  const selectedStored = activeBrand?.themes.find((t) => t.id === selectedThemeId);
  const dirty =
    !!activeBrand &&
    !!selectedStored &&
    (JSON.stringify(draft.theme) !== JSON.stringify(selectedStored) ||
      JSON.stringify(draft.terms) !== JSON.stringify(activeBrand.terms));

  /** Replace a brand in local state with a freshly-mapped server copy. */
  const replaceBrand = React.useCallback((updated: Brand) => {
    setBrands((bs) => bs.map((b) => (b.id === updated.id ? updated : b)));
  }, []);

  /** Load a brand + one of its themes into the editor draft. */
  const openDraft = React.useCallback((brand: Brand, themeId?: string) => {
    const theme = resolveTheme(brand, themeId);
    setSelectedThemeId(theme.id);
    setDraft({ theme: clone(theme), terms: clone(brand.terms) });
  }, []);

  /** Merge a server brand into local state (add or refresh in place). */
  const upsertBrand = React.useCallback((ui: Brand) => {
    setBrands((bs) => (bs.some((b) => b.id === ui.id) ? bs.map((b) => (b.id === ui.id ? ui : b)) : [...bs, ui]));
  }, []);

  const cacheBrand = React.useCallback(
    (brand: BrandData) => upsertBrand(toUiBrand(brand)),
    [upsertBrand],
  );

  /** Find-or-fetch a brand into local state (see interface docs). The fetch path
   *  covers brands the viewer doesn't belong to — super admins opening any brand
   *  by URL — relying on the backend's `brand(id)` superuser access. */
  const resolveBrand = React.useCallback(
    async (brandId: string): Promise<boolean> => {
      if (brandsRef.current.some((b) => b.id === brandId)) return true;
      try {
        const data = await fetchBrand(brandId);
        if (!data) return false;
        upsertBrand(toUiBrand(data));
        return true;
      } catch {
        return false;
      }
    },
    [upsertBrand],
  );

  /** Make a loaded brand active and open a theme into the draft. No-op (until a
   *  later resolve) if the brand isn't loaded yet. */
  const applySelection = React.useCallback(
    (brandId: string, themeId?: string) => {
      const brand = brandsRef.current.find((b) => b.id === brandId);
      if (!brand) return;
      setActiveId(brandId);
      openDraft(brand, themeId);
    },
    [openDraft],
  );

  const commitBrand = React.useCallback(async (input: NewBrandInput): Promise<string | null> => {
    try {
      const created = await apiCreateBrand({
        name: input.name,
        initials: input.initials,
        terminology: toTerminologyInput(input.terms),
        // The chosen starter palette seeds the brand's default (live) theme.
        startingPalette: {
          header: input.theme.header,
          background: input.theme.background,
          surface: input.theme.surface,
          primary: input.theme.primary,
          accent: input.theme.accent,
          text: input.theme.text,
        },
      });
      const brand = toUiBrand(created);
      // Seed local state so the brand is available the instant the caller
      // navigates to /b/:id/theming; BrandRoute then re-applies the selection.
      setBrands((bs) => [...bs, brand]);
      setActiveId(brand.id);
      const theme = resolveTheme(brand);
      setSelectedThemeId(theme.id);
      setDraft({ theme: clone(theme), terms: clone(brand.terms) });
      toast.success("“" + brand.name + "” created — start theming");
      return brand.id;
    } catch (e) {
      toast.error(errorMessage(e, "Couldn’t create that brand."));
      return null;
    }
  }, []);

  /** Delete a brand. Resolves to the next brand to open (or null if none left);
   *  the caller navigates there. Selection itself is URL-driven via BrandRoute. */
  const deleteBrand = React.useCallback(
    async (brandId: string): Promise<string | null> => {
      const target = brandsRef.current.find((b) => b.id === brandId);
      try {
        await apiDeleteBrand(brandId);
        const remaining = brandsRef.current.filter((b) => b.id !== brandId);
        setBrands(remaining);
        toast.success("“" + (target?.name ?? "Brand") + "” deleted");
        return remaining[0]?.id ?? null;
      } catch (e) {
        toast.error(errorMessage(e, "Couldn’t delete that brand."));
        return brandId; // stay put on failure
      }
    },
    [],
  );

  /** Add a fresh theme to the active brand. Resolves to the new theme id so the
   *  caller can navigate `?theme=` to it. */
  const addTheme = React.useCallback(async (): Promise<string | undefined> => {
    if (!activeBrand) return undefined;
    try {
      const created = await apiCreateTheme({
        brandId: activeBrand.id,
        name: `New Theme ${activeBrand.themes.length + 1}`,
      });
      const theme = toUiTheme(created);
      setBrands((bs) => bs.map((b) => (b.id === activeId ? { ...b, themes: [...b.themes, theme] } : b)));
      setSelectedThemeId(theme.id);
      setDraft({ theme: clone(theme), terms: clone(activeBrand.terms) });
      toast.success("“" + theme.name + "” added");
      return theme.id;
    } catch (e) {
      toast.error(errorMessage(e, "Couldn’t add a theme."));
      return undefined;
    }
  }, [activeBrand, activeId]);

  /** Rename a theme in place (applies immediately, outside the save/draft flow). */
  const renameTheme = React.useCallback(
    async (themeId: string, name: string) => {
      const trimmed = name.trim();
      if (!trimmed || !activeBrand) return;
      const stored = activeBrand.themes.find((t) => t.id === themeId);
      if (!stored || stored.name === trimmed) return;
      try {
        // A rename is a whole-theme write of the stored (published) content with
        // a new name — it must not publish any unsaved draft colour edits.
        const updated = await apiUpdateTheme({
          brandId: activeBrand.id,
          themeId,
          content: { ...toThemeContentInput(stored), name: trimmed },
        });
        const uiTheme = toUiTheme(updated);
        setBrands((bs) =>
          bs.map((b) =>
            b.id === activeId
              ? { ...b, themes: b.themes.map((t) => (t.id === themeId ? uiTheme : t)) }
              : b,
          ),
        );
        // Keep the open draft's name in sync so the rename doesn't read as dirty.
        setDraft((d) => (themeId === selectedThemeId ? { ...d, theme: { ...d.theme, name: trimmed } } : d));
      } catch (e) {
        toast.error(errorMessage(e, "Couldn’t rename that theme."));
      }
    },
    [activeBrand, activeId, selectedThemeId],
  );

  /** Delete a theme. Blocked for the last theme and the live theme. Resolves to
   *  the theme to switch to when the deleted one was selected, else undefined. */
  const deleteTheme = React.useCallback(
    async (themeId: string): Promise<string | undefined> => {
      if (!activeBrand) return undefined;
      if (activeBrand.themes.length <= 1) {
        toast.error("A brand needs at least one theme.");
        return undefined;
      }
      if (themeId === activeBrand.activeThemeId) {
        toast.error("Can’t delete the live theme — set another theme live first.");
        return undefined;
      }
      try {
        await apiDeleteTheme(activeBrand.id, themeId);
        const remaining = activeBrand.themes.filter((t) => t.id !== themeId);
        setBrands((bs) => bs.map((b) => (b.id === activeId ? { ...b, themes: remaining } : b)));
        if (themeId === selectedThemeId) {
          const next = resolveTheme({ ...activeBrand, themes: remaining });
          setSelectedThemeId(next.id);
          setDraft({ theme: clone(next), terms: clone(activeBrand.terms) });
          toast.success("Theme deleted");
          return next.id;
        }
        toast.success("Theme deleted");
        return undefined;
      } catch (e) {
        toast.error(errorMessage(e, "Couldn’t delete that theme."));
        return undefined;
      }
    },
    [activeBrand, activeId, selectedThemeId],
  );

  /** Mark a theme as the brand's live theme. */
  const setLiveTheme = React.useCallback(
    async (themeId: string) => {
      if (!activeBrand || themeId === activeBrand.activeThemeId) return;
      const label = activeBrand.themes.find((x) => x.id === themeId)?.name ?? "Theme";
      try {
        const updated = await apiSetActiveTheme(activeBrand.id, themeId);
        replaceBrand(toUiBrand(updated));
        toast.success("“" + label + "” is now live");
      } catch (e) {
        toast.error(errorMessage(e, "Couldn’t set the live theme."));
      }
    },
    [activeBrand, replaceBrand],
  );

  const save = React.useCallback(async () => {
    if (!activeBrand) return;
    setSaving(true);
    try {
      // A freshly-picked logo is held in the draft as a `data:` URL (preview only,
      // not yet stored). Upload it now and swap in the hosted URL before publishing,
      // so the image bytes never go into the publish. An unchanged logo is already an
      // `https:` URL, so it's left as-is — no redundant re-upload.
      let theme = draft.theme;
      if (theme.logoImg?.startsWith("data:")) {
        const logoUrl = await apiUploadThemeLogo({
          brandId: activeBrand.id,
          themeId: selectedThemeId,
          image: theme.logoImg,
        });
        theme = { ...theme, logoImg: logoUrl };
        // Reflect the hosted URL in the draft so a later publish doesn't re-upload.
        setDraft((d) => ({ ...d, theme: { ...d.theme, logoImg: logoUrl } }));
      }
      const updated = await apiPublishBrandTheme({
        brandId: activeBrand.id,
        themeId: selectedThemeId,
        content: toThemeContentInput(theme),
        terminology: toTerminologyInput(draft.terms),
      });
      replaceBrand(toUiBrand(updated));
      setLastSaved(true);
      // The embedded live previews don't poll, so force them to reload now that
      // the brand's published theme has changed.
      setPreviewReloadKey((k) => k + 1);
      toast.success("Published to " + activeBrand.name);
    } catch (e) {
      toast.error(errorMessage(e, "Couldn’t publish your changes."));
    } finally {
      setSaving(false);
    }
  }, [activeBrand, selectedThemeId, draft, replaceBrand]);

  const discard = React.useCallback(() => {
    if (!activeBrand) return;
    setDraft({ theme: clone(resolveTheme(activeBrand, selectedThemeId)), terms: clone(activeBrand.terms) });
  }, [activeBrand, selectedThemeId]);

  const onThemeChange = React.useCallback((key: string, value: string | null) => {
    setDraft((d) => ({ ...d, theme: { ...d.theme, [key]: value } }));
  }, []);

  const onTermChange = React.useCallback((key: keyof Terms, value: string) => {
    setDraft((d) => ({ ...d, terms: { ...d.terms, [key]: value } }));
  }, []);

  /**
   * Verify portal access for the currently signed-in Firebase user and seed the
   * brand list. Distinguishes three outcomes:
   *   - reachable + authorized  → enter the portal
   *   - reachable + unauthorized → sign out with an explanatory error
   *   - UNREACHABLE              → keep the Firebase session, flag the backend as
   *                                unavailable, and let the interstitial offer a
   *                                retry. Losing the backend is not a sign-out.
   * Reused by the auth effect and by the interstitial's "Try again" button.
   */
  const runSessionCheck = React.useCallback(async () => {
    const auth = getFirebaseAuth();
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      setAuthed(false);
      setBackendUnavailable(false);
      setAccessDenied(false);
      setAuthChecking(false);
      setAuthReady(true);
      return;
    }

    setAuthChecking(true);
    try {
      const profile = await fetchSelf();
      if (!profile) {
        // Signed in, reachable backend, but no portal access. Keep the session —
        // the partners-only page explains how to get access; they sign out
        // explicitly when they're ready to leave.
        setAuthed(false);
        setBackendUnavailable(false);
        setAccessDenied(true);
        setAuthError(null);
        return;
      }

      // Access has two orthogonal axes: the global `partner` role (gates brand
      // creation) and ≥1 brand membership (gates editing brands you belong to).
      const isPartner = hasPartnerAccess(profile.roles);
      let loadedBrands: Brand[] = [];
      try {
        const connection = await fetchMyBrands();
        loadedBrands = connection.nodes.map(toUiBrand);
      } catch {
        // A brand-lookup failure (e.g. the field isn't deployed yet) is tolerated;
        // since `self` just succeeded, the backend is reachable. Fall back to the
        // role check alone.
      }

      const allowed = isPartner || loadedBrands.length > 0;
      if (!allowed) {
        // Authenticated but not a partner and not a member of any brand. Stay
        // signed in and show the partners-only page (see above).
        setUser({
          name: profile.displayName || firebaseUser.displayName || firebaseUser.email || "",
          email: firebaseUser.email || firebaseUser.phoneNumber || (profile.username ? "@" + profile.username : ""),
          avatarUrl: profile.photoURL,
          username: profile.username ?? "",
        });
        setAuthed(false);
        setBackendUnavailable(false);
        setAccessDenied(true);
        setAuthError(null);
        return;
      }

      setUser({
        name:
          profile.displayName ||
          firebaseUser.displayName ||
          firebaseUser.email ||
          firebaseUser.phoneNumber ||
          "Partner",
        email:
          firebaseUser.email ||
          firebaseUser.phoneNumber ||
          (profile.username ? "@" + profile.username : ""),
        avatarUrl: profile.photoURL,
        username: profile.username ?? "",
      });
      setBrands(loadedBrands);
      setCanCreateBrand(isPartner);
      setIsSuperAdmin(rolesIncludeSuperAdmin(profile.roles));
      // No active-brand seeding here: the landing redirect picks a brand from the
      // URL, and BrandRoute applies the selection (`:brandId` + `?theme`). The
      // editor draft stays at its harmless empty default until then.
      setBackendUnavailable(false);
      setAccessDenied(false);
      setAuthed(true);
      setAuthError(null);
    } catch (e) {
      if (isBackendUnavailable(e)) {
        // Signed in at Firebase, but we can't reach the backend to load the
        // portal. Keep the session — the user is logged in, there's just nothing
        // they can do until the backend is back. The interstitial offers a retry.
        setBackendUnavailable(true);
        setAuthed(false);
        setAuthError(null);
      } else {
        setBackendUnavailable(false);
        setAuthed(false);
        setAuthError("We couldn’t verify your account access. Please try again.");
        await signOut(auth);
      }
    } finally {
      setAuthChecking(false);
      setAuthReady(true);
    }
  }, []);

  // Drive auth from Firebase: re-run the access check on every auth-state change.
  React.useEffect(() => {
    if (!FIREBASE_ENABLED) return;
    const auth = getFirebaseAuth();

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setAuthed(false);
        setAuthChecking(false);
        setUser(GUEST_USER);
        setBrands([]);
        setCanCreateBrand(false);
        setIsSuperAdmin(false);
        setActiveId("");
        setBackendUnavailable(false);
        setAccessDenied(false);
        setAuthReady(true);
        return;
      }
      await runSessionCheck();
    });

    return () => unsubscribe();
  }, [runSessionCheck]);

  const clearAuthError = React.useCallback(() => setAuthError(null), []);

  const logout = React.useCallback(() => {
    setAuthError(null);
    if (FIREBASE_ENABLED) void signOut(getFirebaseAuth());
  }, []);

  const value: PortalContextValue = {
    authed, authReady, authChecking, authError, backendUnavailable, retryBackend: runSessionCheck,
    accessDenied, user, clearAuthError, logout,
    brands, activeId, activeBrand, viewingOthersBrand, canCreateBrand, isSuperAdmin,
    resolveBrand, applySelection, cacheBrand, commitBrand, deleteBrand,
    selectedThemeId, addTheme, renameTheme, deleteTheme, setLiveTheme,
    draft, dirty, saving, lastSaved, onThemeChange, onTermChange, save, discard,
    defs: TOKEN_DEFS, previewReloadKey,
    welcome, dismissWelcome: () => undefined,
  };

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
}

export function usePortal() {
  const ctx = React.useContext(PortalContext);
  if (!ctx) throw new Error("usePortal must be used within a PortalProvider");
  return ctx;
}
